/**
 * balanceSounds.ts — Sound previews for the Balance dashboard (/balance).
 *
 * Maps each balance entry (weapon / enemy / item id) to the sound file(s) it
 * uses, so the dashboard can show a ▶ button per sound. One click = play once
 * (no loop), and starting a new preview stops the previous one.
 *
 * Only file-backed sounds are listed (a few in-game sounds are synthesized in
 * Web Audio and have no file to preview — those are intentionally omitted).
 */


export interface PreviewSound { label: string; file: string; }

const S = (label: string, name: string): PreviewSound => ({ label, file: `/sounds/${name}` });

/** entry id (weapon id / enemy definitionId / item id) → its sounds. */
export const BALANCE_SOUND_MAP: Record<string, PreviewSound[]> = {
  // ── WEAPONS (fire / reload / hit / throw / swing) ──
  revolver:            [S('Fire', 'revolver_shot.wav'), S('Reload', 'revolver_reload.mp3'), S('Empty', 'revolver_empty.mp3')],
  peanut_rifle:        [S('Fire', 'sniper_shot.wav'), S('Reload', 'rifle_reload.mp3')],
  spatula_slash:       [S('Swing', 'spatchula-sound-1.wav'), S('Hit', 'spatula_hit.mp3')],
  fireball_wand:       [S('Fire', 'fireball_wand_fire.wav'), S('Hit', 'fireball_wand_hit.mp3')],
  viking_axe:          [S('Throw', 'viking_axe_throw.wav'), S('Hit', 'viking_axe_hit.mp3')],
  voogoo_rager_toss:   [S('Throw', 'voogoo_rager_toss_throw.wav')],
  tactical_basketball: [S('Bounce', 'basketball_bounce.wav')],
  // chad_aura — intentionally NO entry: Dylan wants the aura silent except the
  // enemy grunts when it hits (loop removed + hitmarker suppressed for aura).
  blunderbuss:         [S('Fire', 'blunderbuss_fire.wav')],
  wpn_ultra_beam:      [S('Fire', 'ultra_beam_fire.mp3'), S('Hit', 'ultra_beam_hit.mp3')],
  wpn_the_terminator:  [S('Fire', 'wpn_the_terminator_fire.wav'), S('Hit', 'terminator_hit.mp3')],
  wpn_dracs_fang:      [S('Swing', 'dracs_fang_swing.mp3')],
  wpn_frozen_darts:    [S('Fire', 'frozen_darts_fire.wav'), S('Hit', 'frozen_darts_hit.mp3')],
  wpn_hurray:          [S('Fire', 'hurray_fire.wav')],
  wpn_ex_girlfriend:   [S('Fire', 'ex_girlfriend_fire.mp3')],
  wpn_karen_drones:    [S('Loop', 'karen_drones_loop.mp3'), S('Hit', 'karen_drones_hit.mp3')],
  wpn_poop_sling:      [S('Fire', 'poop_sling_fire.mp3'), S('Hit', 'poop_sling_hit.mp3')],
  wpn_shitty_cannon:   [S('Fire', 'shitty_cannon_fire.wav'), S('Hit', 'shitty_cannon_hit.mp3')],
  wpn_squirter:        [S('Fire', 'squirter_fire.mp3'), S('Reload', 'squirter_reload.mp3')],
  wpn_ugly_kittens:    [S('Spawn', 'kittens_spawn.wav'), S('Attack', 'kittens_attack.wav')],

  // ── ENEMIES (hit / death) ──
  zombie:      [S('Hit', 'zombie_hit.wav'), S('Grunt', 'zombie_grunt.wav'), S('Death', 'zombie_death.wav')],

  // ── ITEMS (the ones with their own sounds) ──
  item_whoopie_cushion_of_doom:    [S('Throw', 'whoopie_throw.mp3'), S('Pop', 'whoopie_pop_burst.mp3')],
  item_asmons_desk_banana:         [S('Throw', 'banana_throw.mp3'), S('Burst', 'banana_bomb_burst.mp3')],
  item_liar_deceiver_false_prophet:[S('Nuke', 'liar_nuke_blast.mp3')],
  item_iranian_bird: [S('Zap', 'iranian_bird_fire.mp3')],  // drone auto-fire
  R08: [S('Rocket', 'rocket_sound.wav')],        // Anus Rocket
  R10: [S('Thorns', 'morning_wood_thorns.mp3')], // Morning Wood
  R11: [S('Gas', 'gas.wav')],                    // Diarrhea Gas
};

/**
 * 🥜 PEANUT VOICE — audition bench.
 *
 * Real TheBurntPeanut clips, scouted from his channel + trimmed to short bites
 * (peanut-scout.mjs → read transcript → peanut-cut.mjs). Dylan auditions these
 * in /balance, picks the keepers, and they get wired into in-game trigger
 * moments (run start / level up / boss kill / kill streak / low HP / death).
 * NOT yet wired to gameplay — this list is the listen-and-pick bench only.
 */
export interface PeanutClip { label: string; file: string; cat: string; }
// Built from the GENERATED peanutVoiceData (213 real clips), grouped by the
// in-game moment that fires them, profanity flagged. Sorted by category so the
// audition panel reads top-to-bottom in trigger order.
const _CAT_LABEL: Record<string, string> = {
  run_start: '🏁 Run start', level_up: '⬆️ Level up', kill: '💀 Kill', kill_streak: '🔥 Kill streak',
  boss_kill: '🏆 Boss kill', low_hp: '🩸 Low HP', death: '☠️ Death', loot: '💰 Loot',
  reaction: '😮 Reaction', trash: '😤 Trash talk', deadpan: '😐 Deadpan',
};
const _CAT_ORDER = ['run_start','level_up','kill','kill_streak','boss_kill','low_hp','death','loot','reaction','trash','deadpan'];
// 23.42 — peanut voice clips DELETED from the game (Dylan). Empty list keeps
// the gold panel compiling; the section renders nothing.
export const PEANUT_VOICE: PeanutClip[] = [];

// Stop the previous preview when a new one starts, so they don't pile up.
let _previewAudio: HTMLAudioElement | null = null;

/** Play a sound file ONCE (no loop). One click = one play. */
export function playBalanceSound(file: string): void {
  try { _previewAudio?.pause(); } catch { /* ignore */ }
  const a = new Audio(file);
  a.volume = 0.85;
  _previewAudio = a;
  a.play().catch(() => { /* autoplay/load errors are non-fatal in a dev tool */ });
}
